/* @ngInject */

export default (
  $q,
  $filter,
  $window,
  $rootScope,
  logger,
  TicketProjectHistory,
  TICKET
) => {
  var order = {
    direction: true,
    property: 'dateDue'
  };

  /**
   * Retrieve the height for the board.
   *
   * @param {Object} _workflow - Workflow to get stages and tickets from.
   * @param {Object} [_isFixed] - Whether the number of tickets is fixed and passed as first param.
   * @param {Number} [_ticketHeight] - Height for the single ticket tile.
   *
   * @returns {Object} - CSS Object with height in pixels.
   */
  function getBoardHeight(_workflow, _isFixed, _ticketHeight) {
    var windowHeight = angular.element($window).height();
    var ticketsNumber = 0;
    var ticketHeight = isNaN(parseFloat(_ticketHeight))
      ? TICKET.UI.SINGLE_TICKET_HEIGHT_COMPACT
      : _ticketHeight;
    if (!!_isFixed) {
      ticketsNumber = !_workflow ? 0 : _workflow.length;
    } else {
      ticketsNumber =
        !_workflow || !_workflow.stages
          ? 0
          : _.maxBy(
              _.reject(_workflow.stages, function (stage) {
                return stage.isCollapsed === true;
              }),
              function (stage) {
                return stage.tickets.length;
              }
            ).tickets.length;
    }
    var stageMaxHeight = ticketsNumber * ticketHeight + TICKET.UI.BOARD_DIFFERENCE_HEIGHT;
    return windowHeight - TICKET.UI.BOARD_DIFFERENCE_HEIGHT - TICKET.UI.BOARD_HEADER_HEIGHT >
      stageMaxHeight
      ? { height: windowHeight - TICKET.UI.BOARD_HEADER_HEIGHT + 'px' }
      : { height: stageMaxHeight + 'px' };
  }

  /**
   * Get ticket project status for given stage.
   *
   * @param {Object} _ticket - Tickets.
   * @param {String} _idStage - Stage ID.
   *
   * @returns {Object} - Ticket project status.
   */
  function getTicketProjectStatus(_ticket, _idStage) {
    if (!_ticket.projectHistory || !_ticket.projectHistory.length) {
      return TICKET.PROJECT_AVAILABLE_STATUSES[0];
    } else {
      var statusItem = $filter('filter')(_ticket.projectHistory, { idStage: _idStage })[0];
      return $filter('filter')(TICKET.PROJECT_AVAILABLE_STATUSES, {
        id: !statusItem ? 0 : statusItem.idStatus
      })[0];
    }
  }

  /**
   * Retrieve the value of tickets.
   *
   * @param {Array} _tickets - Tickets.
   * @param {Number} _probability - Probability to use for value calculation.
   * @param {Boolean} [_format] - Should value be formatted with currency?
   *
   * @returns {Number|String} - Value as a number, or formatted with currency.
   */
  function getTicketsSumValue(_tickets, _probability, _format) {
    var value =
      !_tickets || !_tickets.length
        ? 0
        : _.reduce(
            _tickets,
            function (memo, ticket) {
              return memo + ticket.getValue(_probability, null);
            },
            0
          );
    return !_format ? value : value.toString().formatWithCurrency($rootScope.CurrentCurrency());
  }

  /**
   * Initialize Project.
   * Sort tickets.
   *
   * @param {Array} _tickets - Tickets.
   */
  function projectInitialize(_tickets) {
    var deferred = $q.defer();

    if (!_tickets) {
      logger.log('projectService', 'projectInitialize', 'Waiting for tickets data');
      deferred.reject();
    } else {
      logger.log(
        'projectService',
        'projectInitialize',
        'Processing tickets for current workflow'
      );
      ticketsSort(_tickets);
      logger.log(
        'projectService',
        'projectInitialize',
        'Processed tickets for current workflow'
      );
      deferred.resolve();
    }

    return deferred.promise;
  }

  /**
   * Set ticket project status history for given stage.
   *
   * @param {Object} _ticket - Tickets.
   * @param {Object} _stage - Stage.
   * @param {Object} _status - Project status.
   */
  function setTicketProjectHistory(_ticket, _stage, _status) {
    var deferred = $q.defer();

    if (!_ticket || !_stage || !_status) {
      logger.error(
        'projectService',
        'setTicketProjectHistory',
        'Missing some of the required information'
      );
      deferred.reject();
    } else {
      logger.log(
        'projectService',
        'setTicketProjectHistory',
        'Setting project history status for ticket',
        _ticket.id
      );
      if (!_ticket.projectHistory || !_ticket.projectHistory.length) {
        _ticket.projectHistory = [];
      } else {
        var index = _.findIndex(_ticket.projectHistory, function (history) {
          return history.idStage === _stage.id;
        });
        if (index > -1) {
          _ticket.projectHistory.splice(index, 1);
        }
      }
      if (_status.id) {
        var statusItem = TicketProjectHistory.blank();
        statusItem.idOwner = $rootScope.CurrentAccount.id;
        statusItem.idStage = _stage.id;
        statusItem.idStatus = _status.id;
        _ticket.projectHistory.push(statusItem);
        deferred.resolve(true);
      }
    }

    return deferred.promise;
  }

  /**
   * Get ordering direction.
   *
   * @returns {Boolean} - Is ordering direction ascending.
   */
  function orderGetDirection() {
    return order.direction;
  }

  /**
   * Get ordering property.
   *
   * @returns {String} - Property to order by.
   */
  function orderGetProperty() {
    return order.property;
  }

  /**
   * Set sorting direction and perform sorting.
   *
   * @param {Array} _tickets - Tickets array to sort.
   * @param {Boolean} _ascending - Set sorting direction.
   */
  function orderSetDirection(_tickets, _ascending) {
    if (order.direction !== _ascending) {
      order.direction = _ascending;
      ticketsSort(_tickets, true);
    }
  }

  /**
   * Set sorting order and perform sorting.
   *
   * @param {Array} _tickets - Tickets array to sort.
   * @param {String} _property - Set sorting order by this property.
   */
  function orderSetProperty(_tickets, _property) {
    if (order.property !== _property) {
      order.property = _property;
      ticketsSort(_tickets);
    }
  }

  /**
   * Sort tickets.
   *
   * @param {Array} _tickets - Tickets array to sort.
   * @param {Boolean} [_reverse] - Do not sort just reverse existing sort order.
   */
  function ticketsSort(_tickets, _reverse) {
    if (_reverse === true) {
      _tickets.reverse();
      return;
    }
    switch (orderGetProperty()) {
      case 'name':
        _tickets.sort(function (a, b) {
          if (a.getName().toLowerCase() < b.getName().toLowerCase()) {
            return -1;
          }
          if (a.getName().toLowerCase() > b.getName().toLowerCase()) {
            return 1;
          }
          return 0;
        });
        break;
      case 'dateEntry':
        _tickets.sort(function (a, b) {
          if (a.timeStamp < b.timeStamp) {
            return -1;
          }
          if (a.timeStamp > b.timeStamp) {
            return 1;
          }
          return 0;
        });
        break;
      case 'dateDue':
        _tickets.sort(function (a, b) {
          if (a.values.onceOffDueDate < b.values.onceOffDueDate) {
            return -1;
          }
          if (a.values.onceOffDueDate > b.values.onceOffDueDate) {
            return 1;
          }
          return 0;
        });
        break;
      default:
        _tickets.sort(function (a, b) {
          if (
            getTicketProjectStatus(a, orderGetProperty()).id >
            getTicketProjectStatus(b, orderGetProperty()).id
          ) {
            return -1;
          }
          if (
            getTicketProjectStatus(a, orderGetProperty()).id <
            getTicketProjectStatus(b, orderGetProperty()).id
          ) {
            return 1;
          }
          return 0;
        });
        break;
    }
    if (order.direction === false) {
      _tickets.reverse();
    }
  }

  var service = {
    getBoardHeight: getBoardHeight,
    getTicketProjectStatus: getTicketProjectStatus,
    getTicketsSumValue: getTicketsSumValue,
    projectInitialize: projectInitialize,
    setTicketProjectHistory: setTicketProjectHistory,
    orderGetDirection: orderGetDirection,
    orderGetProperty: orderGetProperty,
    orderSetDirection: orderSetDirection,
    orderSetProperty: orderSetProperty,
    ticketsSort: ticketsSort
  };
  return service;
};
